import clsx from 'clsx'
import { PropertyValueMap, unsafeCSS } from 'lit'
import { property, query } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { CommonFormElementSize, CommonSize } from '@/common/types'
import { LabeledChoiceElement } from '@/internal/mixin/labeledChoiceElement'

import DapDSFeedback from '../feedback/feedback.component'
import DapDSFormLabel from '../form/form-label/form-label.component'
import DapDSTypography from '../typography/typography.component'
import styles from './_styles/radio-button.scss?inline'

// Detected once at module load — avoids per-update property lookup.
const SUPPORTS_ARIA_DESCRIPTION =
  typeof ElementInternals !== 'undefined' &&
  'ariaDescription' in ElementInternals.prototype

/**
 * `dap-ds-radio-button`
 * @summary A radio button is a graphical control element that allows the user to choose only one of a predefined set of mutually exclusive options.
 * @element dap-ds-radio-button
 * @title - Radio button
 *
 * @event {{ checked: boolean, value: string, disabled: boolean, readonly: boolean, type: 'radio' }} dds-change - Fired when the radio button is checked.
 * @event {{ void }} dds-blur - Emitted when the radio button loses focus.
 * @event {{ void }} dds-focus - Emitted when the radio button gains focus.
 * @event {{ checked: boolean, value: string, disabled: boolean, readonly: boolean }} dds-input - Emitted when the radio button receives input.
 *
 * @property {string} name - The name of the radio button.
 * @property {string} value - The value of the radio button.
 * @property {boolean} checked - Whether the radio button is checked.
 * @property {string} label - The label of the radio button.
 * @property {boolean} hideLabel - Visually hides the label while keeping it available to assistive technology. (default: false)
 * @property {string} description - The description of the radio button.
 * @property {boolean} disabled - Whether the radio button is disabled.
 * @property {boolean} readonly - Whether the radio button is readonly (cannot be changed but value is submitted with form).
 * @property {boolean} required - Whether the radio button is required.
 * @property {'xs' | 'sm' | 'lg'} size - The size of the radio button. Default is 'sm'.
 * @property {'left' | 'right'} labelPlacement - The placement of the label.
 * @property {'top' | 'bottom'} descriptionPlacement - The placement of the description.
 * @property {boolean} subtle - The weight of the label.
 * @property {string} feedback - The feedback of the radio button.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The feedback type of the radio button.
 * @property {boolean} invalid - The invalid state of the radio button.
 * @property {boolean} optional - The optional state of the radio button.
 * @property {string} optionalLabel - The optional label of the radio button.
 * @property {string} requiredLabel - The required indicator of the radio button. (default: '*')
 * @property {boolean} preventDefault - Whether the radio button should prevent the default action.
 * @property {boolean} focusable - Whether the radio button is focusable.
 * @property {'normal' | 'background'} type - The type of the radio button.
 * @property {boolean} border - This sets up a border around the radio button, when true.
 *
 * @csspart base - The main radio button container.
 * @csspart wrapper - The wrapper of the radio button.
 * @csspart base-label - The main label container.
 * @csspart label - The label of the radio button.
 * @csspart input - The native input of the radio button.
 * @csspart control - The control of the radio button.
 * @csspart label-container - The label container of the radio button.
 * @csspart description - The description of the radio button.
 *
 * @cssproperty --dds-radio-size - Sets the size of the radio button. (default: var(--dds-spacing-600))
 * @cssproperty --dds-radio-border-width - Sets the border width of the radio button. (default: var(--dds-border-width-large))
 * @cssproperty --dds-radio-border-radius - Sets the border radius of the radio button. (default: var(--dds-radius-rounded))
 * @cssproperty --dds-radio-border-color - Sets the border color of the radio button. (default: var(--dds-border-neutral-base))
 * @cssproperty --dds-radio-background-color - Sets the background color of the radio button. (default: var(--dds-background-neutral-base))
 * @cssproperty --dds-radio-hover-border-color - Sets the border color of the radio button on hover. (default: var(--dds-border-neutral-medium))
 * @cssproperty --dds-radio-hover-background-color - Sets the background color of the radio button on hover. (default: var(--dds-background-neutral-medium))
 * @cssproperty --dds-radio-active-border-color - Sets the border color of the radio button when active. (default: var(--dds-border-neutral-strong))
 * @cssproperty --dds-radio-active-background-color - Sets the background color of the radio button when active. (default: var(--dds-background-neutral-strong))
 * @cssproperty --dds-radio-checked-background-color - Sets the background color of the checked radio button. (default: var(--dds-background-brand-base-inverted))
 * @cssproperty --dds-radio-checked-hover-background-color - Sets the background color of the checked radio button on hover. (default: var(--dds-background-brand-medium-inverted))
 * @cssproperty --dds-radio-checked-active-background-color - Sets the background color of the checked radio button when active. (default: var(--dds-background-brand-strong-inverted))
 * @cssproperty --dds-radio-disabled-background-color - Sets the background color of the disabled radio button. (default: var(--dds-background-neutral-disabled))
 * @cssproperty --dds-radio-icon-background-color - Sets the background color of the radio button icon. (default: var(--dds-transparent-white-strong-static))
 * @cssproperty --dds-radio-disabled-icon-background-color - Sets the background color of the disabled radio button icon. (default: var(--dds-background-neutral-stronger))
 * @cssproperty --dds-radio-invalid-border-color - Sets the border color of the invalid radio button. (default: var(--dds-border-negative-base))
 * @cssproperty --dds-radio-invalid-background-color - Sets the background color of the invalid radio button. (default: var(--dds-background-negative-base))
 * @cssproperty --dds-radio-invalid-hover-border-color - Sets the border color of the invalid radio button on hover. (default: var(--dds-border-negative-medium))
 * @cssproperty --dds-radio-invalid-hover-background-color - Sets the background color of the invalid radio button on hover. (default: var(--dds-background-negative-medium))
 * @cssproperty --dds-radio-invalid-active-border-color - Sets the border color of the invalid radio button when active. (default: var(--dds-border-negative-strong))
 * @cssproperty --dds-radio-invalid-active-background-color - Sets the background color of the invalid radio button when active. (default: var(--dds-background-negative-strong))
 * @cssproperty --dds-radio-invalid-checked-background-color - Sets the background color of the invalid checked radio button. (default: var(--dds-background-negative-base-inverted))
 * @cssproperty --dds-radio-invalid-checked-hover-background-color - Sets the background color of the invalid checked radio button on hover. (default: var(--dds-background-negative-medium-inverted))
 * @cssproperty --dds-radio-invalid-checked-active-background-color - Sets the background color of the invalid checked radio button when active. (default: var(--dds-background-negative-strong-inverted))
 * @cssproperty --dds-radio-icon-size - Sets the size of the radio button icon. (default: var(--dds-spacing-300))
 * @cssproperty --dds-radio-readonly-border-color - Sets the border color when the radio button is readonly. (default: var(--dds-border-neutral-subtle))
 * @cssproperty --dds-radio-readonly-background-color - Sets the background color when the radio button is readonly. (default: var(--dds-background-neutral-subtle))
 * @cssproperty --dds-radio-readonly-icon-color - Sets the color of the radio button icon when readonly. (default: var(--dds-text-neutral-base))
 */
export default class DapDSRadioButton extends LabeledChoiceElement {
  static tagName = 'dap-ds-radio-button'

  static dependencies = {
    'dap-ds-form-label': DapDSFormLabel,
    'dap-ds-typography': DapDSTypography,
    'dap-ds-feedback': DapDSFeedback,
  }

  private static nextId = 0

  private readonly inputId: string
  private readonly labelId: string
  private readonly descriptionId: string

  @query('.radio__input') private readonly input!: HTMLInputElement

  /** Whether the radio button is focusable */
  @property({ type: Boolean }) focusable = false

  /** Position in set — set by radio-group for correct screen reader counting */
  @property({ type: Number }) posInSet?: number

  /** Set size — set by radio-group for correct screen reader counting */
  @property({ type: Number }) setSize?: number

  /** Whether the radio button should prevent the default action */
  @property({ type: Boolean, reflect: true }) preventDefault: boolean = false

  /** Whether the radio button is readonly */
  @property({ type: Boolean, reflect: true }) readonly: boolean = false

  /** This sets up a border around the radio button, when true. */
  @property({ type: Boolean }) border: boolean = false

  /** The type of the radio button
   * @type {'normal' | 'background'}
   */
  @property({ type: String }) type: 'normal' | 'background' = 'normal'

  static readonly styles = unsafeCSS(styles)

  constructor() {
    super()
    DapDSRadioButton.nextId++
    const uniqueSuffix =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID().slice(0, 8)
        : Date.now().toString(36)
    this.inputId = `input-${DapDSRadioButton.nextId}-${uniqueSuffix}`
    this.labelId = `label-${DapDSRadioButton.nextId}-${uniqueSuffix}`
    this.descriptionId = `description-${DapDSRadioButton.nextId}-${uniqueSuffix}`
    // Initialize the feedbackId from FeedbackMixin
    this.feedbackId = `feedback-${DapDSRadioButton.nextId}-${uniqueSuffix}`
    // Set host as the ARIA radio node so screen readers get correct posinset/setsize.
    // Native <input type="radio"> inside shadow DOM cannot be grouped across shadow
    // boundaries, causing browsers to compute "1 of 1" regardless of aria-posinset.
    this.internals.role = 'radio'
    // NOTE: setAttribute('role') cannot be called here — the custom element spec
    // forbids adding attributes in the constructor (React throws NotSupportedError
    // via document.createElement). Moved to connectedCallback instead.
  }

  connectedCallback() {
    super.connectedCallback()
    // Explicit role attribute for screen readers that don't honour ElementInternals.
    // Must be set here, not in the constructor (spec forbids constructor attributes).
    this.setAttribute('role', 'radio')
    this.addEventListener('focus', this.onFocus)
    this.addEventListener('blur', this.onBlur)
    this.addEventListener('keydown', this._onHostKeydown)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('focus', this.onFocus)
    this.removeEventListener('blur', this.onBlur)
    this.removeEventListener('keydown', this._onHostKeydown)
  }

  private _onHostKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
      // Dispatch click on host so the radio-group's click handler selects this radio
      HTMLElement.prototype.click.apply(this)
    }
  }

  private _syncHostAria(): void {
    // Explicit aria-checked on host — internals alone not read by all SRs.
    const checkedStr = this.checked ? 'true' : 'false'
    this.internals.ariaChecked = checkedStr
    this.setAttribute('aria-checked', checkedStr)

    // Chrome overrides internals posInSet/setSize for elements with shadow-DOM
    // same-name radio inputs (computing "1 of 1" per shadow scope). Explicit
    // HTML attributes take precedence and work correctly in all browsers.
    if (this.posInSet != null) {
      this.setAttribute('aria-posinset', String(this.posInSet))
    } else {
      this.removeAttribute('aria-posinset')
    }
    if (this.setSize != null) {
      this.setAttribute('aria-setsize', String(this.setSize))
    } else {
      this.removeAttribute('aria-setsize')
    }
    this.internals.ariaPosInSet =
      this.posInSet != null ? String(this.posInSet) : null
    this.internals.ariaSetSize =
      this.setSize != null ? String(this.setSize) : null

    this.internals.ariaDisabled = this.disabled ? 'true' : null
    this.internals.ariaRequired = this.required ? 'true' : null

    // aria-description is ARIA 1.3 and not yet supported in Safari/WebKit.
    // Fall back to merging description into the accessible name for those browsers.
    // Always set explicit aria-label on the host for SRs that don't read internals.
    let accessibleName: string
    if (SUPPORTS_ARIA_DESCRIPTION) {
      this.internals.ariaLabel = this.label || null
      this.internals.ariaDescription = this.description || null
      accessibleName = this.label || ''
    } else {
      const combined = [this.label, this.description].filter(Boolean)
      accessibleName = combined.join('. ')
      this.internals.ariaLabel = accessibleName || null
    }
    if (accessibleName) {
      this.setAttribute('aria-label', accessibleName)
    } else {
      this.removeAttribute('aria-label')
    }

    // Tabindex lives on the host (not the hidden inner input) so the host is
    // the keyboard focus target and screen readers track focus correctly.
    const focusable = (this.focusable || this.checked) && !this.disabled
    this.setAttribute('tabindex', focusable ? '0' : '-1')
  }

  /**
   * Handles click events on the radio button.
   * Manages the checked state and emits change events.
   * @private
   */
  private handleClick() {
    // Early return if click should be prevented, component is disabled, or readonly
    if (this.preventDefault || this.disabled || this.readonly) {
      return
    }

    this.checked = true

    const { checked, value, disabled } = this

    // Emit change event with null safety
    try {
      this.emit('dds-change', {
        checked,
        // `?? ''` not `|| ''` — a radio may legitimately carry `false` or `0`
        value: value ?? '',
        disabled,
        readonly: this.readonly,
        type: 'radio',
      })
    } catch (error) {
      console.error('Error emitting radio button change event:', error)
    }

    // Update validity state with error handling
    try {
      this.setValidity()
    } catch (error) {
      console.error('Error setting radio button validity:', error)
    }
  }

  formResetCallback() {
    this.internals.setValidity({})
    this.internals.setFormValue(this.defaultValue)
    this.value = this.defaultValue
    this.checked = this.defaultValue === this.value
  }

  /**
   * Handles input events on the radio button.
   * Emits the dds-input event with current radio button state.
   * @private
   */
  private handleInput() {
    const { checked, value, disabled } = this

    const event = new CustomEvent('dds-input', {
      bubbles: true,
      cancelable: false,
      composed: true,
      detail: { checked, value, disabled, readonly: this.readonly },
    })

    this.dispatchEvent(event)
  }

  public get focusElement(): HTMLInputElement {
    if (!this.input) {
      throw new Error(
        'Radio button input element not found. Component may not be properly initialized.',
      )
    }
    return this.input
  }

  public focus(): void {
    if (!this.disabled && !this.readonly) {
      HTMLElement.prototype.focus.apply(this)
    }
  }

  public blur(): void {
    HTMLElement.prototype.blur.apply(this)
  }

  /**
   * Handles invalid events on the radio button input.
   * Prevents the default browser validation UI from showing and manages invalid state.
   * @param {Event} event - The invalid event
   * @private
   */
  handleInvalid(event: Event) {
    event.preventDefault()
    // Set invalid state if not already set
    if (!this.invalid) {
      this.invalid = true
    }
  }

  protected updated(
    changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(changedProperties)

    this._syncHostAria()

    // Update invalid state during the update cycle when required or checked changes
    // This prevents scheduling an update after updateComplete
    // Only do this for standalone radio buttons, not when inside a radio-group
    // (radio-groups manage the invalid state of their children)
    const isInRadioGroup = this.closest('dap-ds-radio-group') !== null
    if (
      !isInRadioGroup &&
      (changedProperties.has('required') ||
        changedProperties.has('checked') ||
        changedProperties.has('disabled')) &&
      this.innerInput &&
      this.innerInput instanceof HTMLInputElement
    ) {
      // Use checkValidity to determine if input is invalid
      // Only update if the value would actually change to avoid unnecessary updates
      const shouldBeInvalid = !this.innerInput.checkValidity()
      if (this.invalid !== shouldBeInvalid) {
        this.invalid = shouldBeInvalid
      }
    }
  }

  render() {
    return html`
      <div part="base">
        <label
          part="base-label"
          class=${clsx('radio choice', `radio--${this.size}`, {
            'radio--checked': this.checked,
            'radio--disabled': this.disabled,
            'radio--readonly': this.readonly,
            'radio--required': this.required,
            'choice--readonly': this.readonly,
            'choice--disabled': this.disabled,
            'choice__label--right': this.labelPlacement === 'right',
            'choice__label--left': this.labelPlacement === 'left',
          })}>
          <div
            part="wrapper"
            class=${clsx('input__wrapper', {
              'input__wrapper--background': this.type === 'background',
              'input__wrapper--border': this.border,
            })}>
            <input
              part="input"
              type="radio"
              class=${clsx('radio__input', {
                'radio__input--background': this.type === 'background',
              })}
              id=${this.inputId}
              value=${ifDefined(this.value)}
              .checked=${this.checked}
              .disabled=${this.disabled}
              .required=${this.required}
              ?readonly=${this.readonly}
              tabindex="-1"
              aria-hidden="true"
              aria-checked=${this.checked ? 'true' : 'false'}
              @invalid=${this.handleInvalid}
              @click=${this.handleClick}
              @input=${this.handleInput} />
            <span
              part="control"
              class=${clsx('input__control', {
                'input__control--disabled': this.disabled,
                'input__control--readonly': this.readonly,
                'input__control--invalid': this.invalid,
                'input__control--checked': this.checked,
              })}>
              <slot name="iconcontent">
                <span class="radio__icon"></span>
              </slot>
              <span
                part="high-contrast"
                class=${clsx(
                  'input__control-high-contrast',
                  `input__control-high-contrast--${this.size}`,
                  {
                    'input__control-high-contrast--disabled': this.disabled,
                    'input__control-high-contrast--readonly': this.readonly,
                  },
                )}></span>
            </span>

            <div
              part="label-container"
              class=${clsx('choice__label-container', {
                'choice__label-container--disabled': this.disabled,
                'choice__label-container--readonly': this.readonly,
                'choice__label--bottom': this.descriptionPlacement === 'top',
                'choice__label--top': this.descriptionPlacement === 'bottom',
              })}>
              ${this.label &&
              html`<dap-ds-form-label
                id=${this.labelId}
                class="label"
                part="label"
                .size=${this.size}
                .required=${this.required}
                .requiredLabel=${this.requiredLabel}
                .optional=${this.optional}
                .optionalLabel=${this.optionalLabel}
                aria-hidden="true"
                .disabled=${this.disabled || this.readonly}
                .readonly=${this.readonly}
                .hideLabel=${this.hideLabel}
                label=${ifDefined(this.label)}
                subtle
                .noMargin=${true}
                renderAs="span">
              </dap-ds-form-label>`}
              ${this.description &&
              html`
                <dap-ds-typography
                  class="description"
                  variant="description"
                  part="description"
                  aria-hidden="true"
                  size=${ifDefined(this.size as CommonFormElementSize)}
                  id=${this.descriptionId}>
                  ${this.description}
                </dap-ds-typography>
              `}
            </div>
          </div>
          ${this.feedback &&
          html`
            <dap-ds-feedback
              part="feedback"
              feedbackType=${ifDefined(this.feedbackType)}
              feedbackSize=${ifDefined(this.size) as CommonSize}
              id="feedback"
              .subtle=${this.subtle}>
              ${this.feedback}
            </dap-ds-feedback>
          `}
        </label>
      </div>
    `
  }
}
